import {
  CannedWorkPackagesService,
  CannedWorkTasksService,
} from "@common/client"
import { infiniteQueryOptions } from "@tanstack/react-query"
import { makeAxiosMutation, makeGroupPages, makeQuery, unwrap } from "../utils"

const PAGE_SIZE = 20

type TaskListParams = Omit<
  Parameters<typeof CannedWorkTasksService.listTasks>[0],
  "skip" | "limit"
>

type PackageListParams = Omit<
  Parameters<typeof CannedWorkPackagesService.listPackages>[0],
  "skip" | "limit"
>

export const cannedWorkTasksBaseKey = ["canned-work-tasks"] as const

export const cannedWorkTaskKey = (id: string) =>
  ["canned-work-task", id] as const

export const cannedWorkPackagesBaseKey = ["canned-work-packages"] as const

export const cannedWorkPackageKey = (id: string) =>
  ["canned-work-package", id] as const

export const cannedPartSuggestionsBaseKey = [
  "canned-work-task-part-suggestions",
] as const

function nextPage<T extends { count: number }>(
  lastPage: T,
  allPages: T[],
): number | undefined {
  const loaded = allPages.length * PAGE_SIZE
  return loaded < lastPage.count ? loaded : undefined
}

export const cannedWorkTasksInfiniteQuery = (params: TaskListParams) =>
  infiniteQueryOptions({
    queryKey: [...cannedWorkTasksBaseKey, "infinite", params],
    queryFn: ({ pageParam }) =>
      CannedWorkTasksService.listTasks({
        ...params,
        skip: pageParam,
        limit: PAGE_SIZE,
      }).then(unwrap),
    initialPageParam: 0,
    getNextPageParam: nextPage,
  })

export const cannedWorkTaskGroupPages = makeGroupPages({
  serviceCall: (params: TaskListParams, page) =>
    CannedWorkTasksService.listTasks({ ...params, ...page }),
  queryKey: (params) => [...cannedWorkTasksBaseKey, "grouped", params],
  pageSize: PAGE_SIZE,
})

export const cannedWorkTasksQuery = makeQuery({
  serviceCall: CannedWorkTasksService.listTasks,
  queryKey: (params) => [...cannedWorkTasksBaseKey, "list", params] as const,
})

export const cannedWorkTaskQuery = makeQuery({
  serviceCall: CannedWorkTasksService.getTask,
  queryKey: (params) => cannedWorkTaskKey(params.task_id),
})

export const cannedPartSuggestionsQuery = makeQuery({
  serviceCall: CannedWorkTasksService.suggestParts,
  queryKey: ({ task_id, prefix }) =>
    [...cannedPartSuggestionsBaseKey, task_id, prefix ?? ""] as const,
})

export const cannedWorkPackagesInfiniteQuery = (params: PackageListParams) =>
  infiniteQueryOptions({
    queryKey: [...cannedWorkPackagesBaseKey, "infinite", params],
    queryFn: ({ pageParam }) =>
      CannedWorkPackagesService.listPackages({
        ...params,
        skip: pageParam,
        limit: PAGE_SIZE,
      }).then(unwrap),
    initialPageParam: 0,
    getNextPageParam: nextPage,
  })

export const cannedWorkPackageGroupPages = makeGroupPages({
  serviceCall: (params: PackageListParams, page) =>
    CannedWorkPackagesService.listPackages({ ...params, ...page }),
  queryKey: (params) => [...cannedWorkPackagesBaseKey, "grouped", params],
  pageSize: PAGE_SIZE,
})

export const cannedWorkPackagesQuery = makeQuery({
  serviceCall: CannedWorkPackagesService.listPackages,
  queryKey: (params) => [...cannedWorkPackagesBaseKey, "list", params] as const,
})

export const cannedWorkPackageQuery = makeQuery({
  serviceCall: CannedWorkPackagesService.getPackage,
  queryKey: (params) => cannedWorkPackageKey(params.package_id),
})

const taskKeys = (taskId?: string) => [
  cannedWorkTasksBaseKey,
  cannedWorkPackagesBaseKey,
  ["canned-work-package"],
  ...(taskId ? [cannedWorkTaskKey(taskId)] : []),
]

export const useCreateCannedWorkTaskMutation = makeAxiosMutation({
  serviceCall: CannedWorkTasksService.create,
  invalidates: () => taskKeys(),
})

export const useCreateCannedWorkTaskVersionMutation = makeAxiosMutation({
  serviceCall: CannedWorkTasksService.createVersion,
  invalidates: (params) => taskKeys(params.task_id),
})

export const useReviewCannedWorkTaskVersionMutation = makeAxiosMutation({
  serviceCall: CannedWorkTasksService.reviewVersion,
  invalidates: (params) => taskKeys(params.task_id),
})

export const useUndoCannedWorkTaskApprovalMutation = makeAxiosMutation({
  serviceCall: CannedWorkTasksService.undoApproval,
  invalidates: (params) => taskKeys(params.task_id),
})

export const useReviewCannedWorkTaskVersionsMutation = makeAxiosMutation({
  serviceCall: CannedWorkTasksService.reviewVersions,
  invalidates: () => [...taskKeys(), ["canned-work-task"]],
})

export const useDeleteCannedWorkTaskMutation = makeAxiosMutation({
  serviceCall: CannedWorkTasksService.deleteTask,
  invalidates: (params) => taskKeys(params.task_id),
})

export const useRestoreCannedWorkTaskMutation = makeAxiosMutation({
  serviceCall: CannedWorkTasksService.restoreTask,
  invalidates: (params) => taskKeys(params.task_id),
})

export const useArchiveCannedWorkTasksMutation = makeAxiosMutation({
  serviceCall: CannedWorkTasksService.archiveMany,
  invalidates: () => [...taskKeys(), ["canned-work-task"]],
})

export const useRestoreCannedWorkTasksMutation = makeAxiosMutation({
  serviceCall: CannedWorkTasksService.restoreMany,
  invalidates: () => [...taskKeys(), ["canned-work-task"]],
})

export const useSetCannedWorkTasksTaskGroupMutation = makeAxiosMutation({
  serviceCall: CannedWorkTasksService.setTaskGroup,
  invalidates: () => [...taskKeys(), ["canned-work-task"]],
})

export const useSetCannedWorkTasksDepartmentMutation = makeAxiosMutation({
  serviceCall: CannedWorkTasksService.setDepartment,
  invalidates: () => [...taskKeys(), ["canned-work-task"]],
})

const packageKeys = (packageId?: string) => [
  cannedWorkPackagesBaseKey,
  ...(packageId ? [cannedWorkPackageKey(packageId)] : []),
]

export const useCreateCannedWorkPackageMutation = makeAxiosMutation({
  serviceCall: CannedWorkPackagesService.create,
  invalidates: () => packageKeys(),
})

export const useUpdateCannedWorkPackageMutation = makeAxiosMutation({
  serviceCall: CannedWorkPackagesService.updatePackage,
  invalidates: (params) => packageKeys(params.package_id),
})

export const useArchiveCannedWorkPackageMutation = makeAxiosMutation({
  serviceCall: CannedWorkPackagesService.archivePackage,
  invalidates: (params) => packageKeys(params.package_id),
})

export const useArchiveCannedWorkPackagesMutation = makeAxiosMutation({
  serviceCall: CannedWorkPackagesService.archiveMany,
  invalidates: () => packageKeys(),
})

export const useRestoreCannedWorkPackagesMutation = makeAxiosMutation({
  serviceCall: CannedWorkPackagesService.restoreMany,
  invalidates: () => packageKeys(),
})

export const useDeleteCannedWorkPackagesMutation = makeAxiosMutation({
  serviceCall: CannedWorkPackagesService.deleteMany,
  invalidates: () => packageKeys(),
})

export const useRestoreCannedWorkPackageMutation = makeAxiosMutation({
  serviceCall: CannedWorkPackagesService.restorePackage,
  invalidates: (params) => packageKeys(params.package_id),
})
